import { defineStore } from 'pinia'
import { getErrorMessage } from '../services/http'

const ACCESS_TOKEN_KEY = 'vms_access_token'
const REFRESH_TOKEN_KEY = 'vms_refresh_token'
const USER_KEY = 'vms_user'
const TENANT_CODE_KEY = 'vms_tenant_code'

const resolveApiBaseUrl = () => {
  const configured = import.meta.env.VITE_API_BASE_URL?.trim()
  if (configured) return configured
  if (typeof window === 'undefined') return ''
  return `${window.location.origin}/api`
}

const apiBaseUrl = resolveApiBaseUrl()

type AuthUser = {
  id: string
  email: string
  username?: string | null
  role: string
  householdId?: string | null
  tenantId: string
  tenantCode?: string | null
  // Community-plan gate flag (Tenant.FeaturesUnlocked), read fresh at login/refresh time.
  // A free/locked tenant has featuresUnlocked=false; SUPER_ADMIN always bypasses the gate
  // regardless of this value (see isSuperAdmin getter below).
  featuresUnlocked?: boolean
}

type AuthResponse = {
  accessToken: string
  refreshToken: string
  accessTokenExpiresIn: string
  refreshTokenExpiresIn: string
  user: AuthUser
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null as AuthUser | null,
    accessToken: null as string | null,
    refreshToken: null as string | null,
    tenantCode: null as string | null,
    loading: false,
    error: null as string | null,
  }),
  getters: {
    isAuthenticated: (state) => Boolean(state.accessToken),
    isSuperAdmin: (state) => state.user?.role === 'SUPER_ADMIN',
    // Community-plan features are unlocked for the user's tenant. SUPER_ADMIN always
    // bypasses the gate (mirrors the backend's [RequiresCommunityPlan] bypass).
    featuresUnlocked: (state) => state.user?.role === 'SUPER_ADMIN' || Boolean(state.user?.featuresUnlocked),
  },
  actions: {
    hydrate() {
      this.accessToken = localStorage.getItem(ACCESS_TOKEN_KEY)
      this.refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
      this.tenantCode = localStorage.getItem(TENANT_CODE_KEY)
      const storedUser = localStorage.getItem(USER_KEY)
      this.user = storedUser ? (JSON.parse(storedUser) as AuthUser) : null
      if (!this.tenantCode && this.user?.tenantCode) {
        this.tenantCode = this.user.tenantCode
        localStorage.setItem(TENANT_CODE_KEY, this.user.tenantCode)
      }
    },
    // Re-fetch the live user/tenant state (esp. featuresUnlocked) so the UI reflects an
    // upgrade/downgrade without a re-login. Called on app boot after hydrate(); a page reload
    // (incl. returning from the PayPal upgrade redirect) refreshes the gate. Non-fatal on failure.
    async refreshMe() {
      const token = this.accessToken ?? localStorage.getItem(ACCESS_TOKEN_KEY)
      if (!token) return
      try {
        const headers: Record<string, string> = { Authorization: `Bearer ${token}` }
        const tenantCode = this.tenantCode ?? localStorage.getItem(TENANT_CODE_KEY)
        if (tenantCode) headers['x-tenant-code'] = tenantCode
        const res = await fetch(`${apiBaseUrl}/auth/me`, { headers })
        if (!res.ok) return
        // /auth/me returns { user: { ..., featuresUnlocked, role } }
        const me = (await res.json()) as { user?: { featuresUnlocked?: boolean; role?: string } }
        if (this.user && me.user) {
          this.user = {
            ...this.user,
            featuresUnlocked: me.user.featuresUnlocked ?? this.user.featuresUnlocked,
            role: me.user.role ?? this.user.role,
          }
          localStorage.setItem(USER_KEY, JSON.stringify(this.user))
        }
      } catch {
        // offline / transient — keep the cached snapshot; the backend still gates real calls
      }
    },
    getAccessToken() {
      return this.accessToken ?? localStorage.getItem(ACCESS_TOKEN_KEY)
    },
    getTenantCode() {
      return this.tenantCode ?? localStorage.getItem(TENANT_CODE_KEY)
    },
    setTenantCode(code: string | null) {
      const normalized = code?.trim() || null
      this.tenantCode = normalized
      if (normalized) {
        localStorage.setItem(TENANT_CODE_KEY, normalized)
      } else {
        localStorage.removeItem(TENANT_CODE_KEY)
      }
    },
    async login(identifier: string, password: string) {
      this.loading = true
      this.error = null
      try {
        const response = await fetch(`${apiBaseUrl}/auth/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ identifier, password }),
        })

        if (!response.ok) {
          const message = await getErrorMessage(response, 'Login failed')
          throw new Error(message)
        }

        const data = (await response.json()) as AuthResponse

        this.applyAuthResponse(data)
      } catch (error) {
        this.error = error instanceof Error ? error.message : 'Login failed'
        throw error
      } finally {
        this.loading = false
      }
    },
    applyAuthResponse(data: AuthResponse) {
      this.accessToken = data.accessToken
      this.refreshToken = data.refreshToken
      this.user = data.user
      this.tenantCode = data.user?.tenantCode ?? null
      localStorage.setItem(ACCESS_TOKEN_KEY, data.accessToken)
      localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken)
      localStorage.setItem(USER_KEY, JSON.stringify(data.user))
      if (this.tenantCode) localStorage.setItem(TENANT_CODE_KEY, this.tenantCode)
      else localStorage.removeItem(TENANT_CODE_KEY)
    },
    async oauthLogin(provider: string, credential: string) {
      this.loading = true; this.error = null
      try {
        const res = await fetch(`${apiBaseUrl}/auth/oauth`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ provider, credential }),
        })
        if (!res.ok) throw new Error(await getErrorMessage(res, 'Sign-in failed'))
        const data = await res.json() as { status: string; auth?: AuthResponse; profile?: unknown }
        if (data.status === 'OK' && data.auth) this.applyAuthResponse(data.auth)
        return data
      } catch (e) { this.error = e instanceof Error ? e.message : 'Sign-in failed'; throw e }
      finally { this.loading = false }
    },
    async registerTenant(payload: Record<string, unknown>) {
      this.loading = true; this.error = null
      try {
        const res = await fetch(`${apiBaseUrl}/auth/register-tenant`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        })
        if (!res.ok) throw new Error(await getErrorMessage(res, 'Registration failed'))
        const data = await res.json() as { status: string; auth?: AuthResponse; tenantId?: string }
        if (data.status === 'OK' && data.auth) this.applyAuthResponse(data.auth)
        return data
      } catch (e) { this.error = e instanceof Error ? e.message : 'Registration failed'; throw e }
      finally { this.loading = false }
    },
    // Confirm a password signup's emailed code; on OK the response carries auth tokens and the
    // admin is logged straight in. Throws (with the server message) on a bad/expired code.
    async verifyRegistration(tenantId: string, code: string) {
      const res = await fetch(`${apiBaseUrl}/auth/verify-registration`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId, code }),
      })
      if (!res.ok) throw new Error(await getErrorMessage(res, 'That code is incorrect. Please try again.'))
      const data = await res.json() as { status: string; auth?: AuthResponse }
      if (data.status === 'OK' && data.auth) this.applyAuthResponse(data.auth)
      return data
    },
    async resendRegistrationCode(tenantId: string) {
      const res = await fetch(`${apiBaseUrl}/auth/resend-registration-code`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId }),
      })
      if (!res.ok) throw new Error(await getErrorMessage(res, 'Please wait a moment before requesting another code.'))
    },
    logout() {
      this.accessToken = null
      this.refreshToken = null
      this.user = null
      this.tenantCode = null
      this.error = null
      localStorage.removeItem(ACCESS_TOKEN_KEY)
      localStorage.removeItem(REFRESH_TOKEN_KEY)
      localStorage.removeItem(USER_KEY)
      localStorage.removeItem(TENANT_CODE_KEY)
    },
  },
})
